import {
  Box,
  Button,
  DefaultErrorMessage,
  dialogErrorImage,
  ErrorBoundary,
  Icon,
  LazyRender,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import { Suspense } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useRepositoryMetaTitle } from "../../app/routing"
import { useRepositoryReleases } from "../../document/documentRouting"
import { DocumentListBodyLayout } from "../DocumentListBody/DocumentListBodyLayout"
import { DocumentListHeader } from "../DocumentListHeader/DocumentListHeader"
import { DocumentListLayout } from "../documentListLayout"
import { DocumentListSearchParamsProvider } from "../DocumentListSearchParamsProvider"
import { DocumentListSidePanel } from "../DocumentListSidePanel"
import { CreateReleaseDialog } from "./CreateReleaseDialog"
import { Release, ReleaseSkeleton } from "./Release"
import { ReleasePageHeader } from "./ReleasePageHeader"

export function ReleasesList() {
  useRepositoryMetaTitle("Planned")

  return (
    <DocumentListSearchParamsProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab="upcoming" />}
        sidePanel={<DocumentListSidePanel selectedMenuItem="main-space" />}
        body={
          <ErrorBoundary renderError={renderReleasesListError}>
            <Suspense fallback={<ReleasesListBodySkeleton />}>
              <ReleasesListBody />
            </Suspense>
          </ErrorBoundary>
        }
      />
    </DocumentListSearchParamsProvider>
  )
}

function ReleasesListBody() {
  const releases = useRepositoryReleases()

  return (
    <DocumentListBodyLayout>
      <ReleasesListBodyHeader />
      {releases.map((release) => (
        <LazyRender key={release.id}>
          <Release release={release} />
        </LazyRender>
      ))}
    </DocumentListBodyLayout>
  )
}

function ReleasesListBodySkeleton() {
  return (
    <DocumentListBodyLayout>
      <ReleasesListBodyHeaderSkeleton />
      <ReleaseSkeleton />
      <ReleaseSkeleton />
      <ReleaseSkeleton />
      <ReleaseSkeleton />
      <ReleaseSkeleton />
      <ReleaseSkeleton />
    </DocumentListBodyLayout>
  )
}

function ReleasesListBodyHeader() {
  const releases = useRepositoryReleases()
  const planned = releases.filter((item) => item.timestamp != null).length
  const unscheduled = releases.length - planned

  const { search } = useLocation()
  const navigate = useNavigate()

  return (
    <ReleasePageHeader
      title={<Text variant="h3">Releases</Text>}
      actions={
        <>
          <Icon name="calendarToday" size="small" color="grey11" />
          <Text
            color="grey11"
            variant="small"
            sx={{
              marginLeft: 4,
            }}
          >
            {planned} Scheduled / {unscheduled} Unscheduled
          </Text>

          <CreateReleaseDialog
            onSuccess={(release) =>
              navigate(`/upcoming/${release.id}${search}`)
            }
          >
            <Button startIcon="add" size="large" sx={{ marginLeft: 16 }}>
              Add Release
            </Button>
          </CreateReleaseDialog>
        </>
      }
    />
  )
}

function ReleasesListBodyHeaderSkeleton() {
  return (
    <ReleasePageHeader
      title={<Skeleton width={200} height={30} />}
      actions={
        <>
          <Skeleton
            key="release-count"
            width={178}
            height={16}
            sx={{ marginRight: 16 }}
          />
          <Skeleton key="add-release" width={142} height={40} />
        </>
      }
    />
  )
}

function renderReleasesListError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching releases."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}
